import { useId, type FormEvent } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import { NumberInput } from '../../ui/NumberInput';
import { Spinner } from '../../../ui/Spinner';
import { btnGhost, btnPrimary, btnSecondary, inputCls } from '../../ui/styles';
import { money } from '../../../core/format/currency';
import { today } from '../../../core/format/dates';
import { locationLabel } from '../../../core/rules/stockMovements';
import { stockLocations, type PurchaseReceipt, type StockItem, type StockLocation } from '../../../core/types/stock';
import { useReceiptForm } from './useReceiptForm';
import { ReceiptFileField } from './ReceiptFileField';
type Props = {
  items: readonly StockItem[];
  suppliers: readonly string[];
  onDone: (receipt: PurchaseReceipt) => void;
  onCancel: () => void;
};
const fieldError = (msg: string | undefined) => msg ? <p role="alert" className="mt-1 text-[12px] text-clay" data-id="element-4639" data-mp="1frd0oj6k30vr">
      {msg}
    </p> : null;

/** Een inkoopfactuur boeken: bijlage, kop en regels; na "Boeken" staat alles in één keer in de voorraad. */
export function ReceiptForm({
  items,
  suppliers,
  onDone,
  onCancel
}: Props) {
  const id = useId();
  const f = useReceiptForm(items);
  const retail = items.filter(i => i.kind === 'retail');
  const consumables = items.filter(i => i.kind === 'verbruik');
  const save = async (e: FormEvent) => {
    e.preventDefault();
    const receipt = await f.save();
    if (!receipt) return;
    toast.success(`${f.pieces} ${f.pieces === 1 ? 'stuk' : 'stuks'} in de voorraad`, {
      description: `Factuur ${receipt.invoiceNumber} van ${receipt.supplier} is geboekt.`
    });
    onDone(receipt);
  };
  return <form onSubmit={e => void save(e)} noValidate className="mb-6 space-y-5 rounded-lg border border-line bg-white p-4 sm:p-5" data-id="element-4640" data-mp="2t62sl2ctv13y">
      <div data-id="element-4641" data-mp="3lw1bsh4cc40p">
        <h2 className="text-[15px] font-medium text-ink" data-id="element-4642" data-mp="3lvjjtwopobyl">Inkoopfactuur boeken</h2>
        <p className="mt-1 text-[13px] text-muted" data-id="element-4643" data-mp="3qnt6n3uj4vwn">Wat er binnenkwam gaat in één keer in de voorraad, met de inkoopprijs. De factuur zelf bewaren we erbij.</p>
      </div>

      <ReceiptFileField file={f.file} preview={f.preview} error={f.fileError ?? f.show('file') ?? null} onChange={f.setFile} data-id="element-4644" data-mp="4bjbsos02mhq" />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-4645" data-mp="3qkkjiu6i2rfy">
        <label htmlFor={`${id}-supplier`} className="block" data-id="element-4646" data-mp="3qmwzx2wm0tqw">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-4647" data-mp="3ll7ohm5h6e2a">Leverancier</span>
          <input id={`${id}-supplier`} list={`${id}-suppliers`} value={f.supplier} onChange={e => f.setSupplier(e.target.value)} maxLength={80} autoComplete="off" className={inputCls} data-id="element-4648" data-mp="3hknomatiowcg" />
          <datalist id={`${id}-suppliers`} data-id="element-4649" data-mp="37u2wn68cmq2i">
            {suppliers.map(s => <option key={s} value={s} data-id="element-4650" data-mp="32vj58qazkm2r" />)}
          </datalist>
          {fieldError(f.show('supplier'))}
        </label>
        <label htmlFor={`${id}-number`} className="block" data-id="element-4651" data-mp="2xvdd790t0xk5">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-4652" data-mp="2tute3zwo7jvd">Factuurnummer</span>
          <input id={`${id}-number`} value={f.invoiceNumber} onChange={e => f.setInvoiceNumber(e.target.value)} maxLength={60} autoComplete="off" className={inputCls} data-id="element-4653" data-mp="2twxq754q3p6q" />
          {fieldError(f.show('invoiceNumber'))}
        </label>
        <label htmlFor={`${id}-date`} className="block" data-id="element-4654" data-mp="1icyz7izqt4zl">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-4655" data-mp="1ibe2bmpyh9yk">Factuurdatum</span>
          <input id={`${id}-date`} type="date" value={f.invoiceDate} max={today()} onChange={e => f.setInvoiceDate(e.target.value)} className={inputCls} data-id="element-4656" data-mp="1nc1tq9i3jstb" />
          {fieldError(f.show('invoiceDate'))}
        </label>
        <label htmlFor={`${id}-location`} className="block" data-id="element-4657" data-mp="ultjd3tsycpa">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-4658" data-mp="zlxz2rmvz3eg">Komt op de plek</span>
          <select id={`${id}-location`} value={f.location} onChange={e => f.setLocation(e.target.value as StockLocation)} className={inputCls} data-id="element-4659" data-mp="14dz9s94eom2d">
            {stockLocations.map(l => <option key={l} value={l} data-id="element-4660" data-mp="bwv98wi3m48v">
                {locationLabel[l]}
              </option>)}
          </select>
        </label>
      </div>

      <fieldset data-id="element-4661" data-mp="2dkho5wevcv7c">
        <legend className="mb-2 text-[13px] font-medium text-ink" data-id="element-4662" data-mp="29kz412uvgo5k">Regels</legend>
        <div className="hidden grid-cols-[minmax(0,1fr)_88px_120px_96px_40px] gap-x-3 pb-1 text-[12px] text-muted sm:grid" aria-hidden="true" data-id="element-4663" data-mp="29iw4ns7txio6">
          <span data-id="element-4664" data-mp="2il5d8cjjf1yx">Artikel</span>
          <span className="text-right" data-id="element-4665" data-mp="2ikvp6ui1z4vv">Aantal</span>
          <span className="text-right" data-id="element-4666" data-mp="2xabavw2q8k4p">Prijs per stuk (€)</span>
          <span className="text-right" data-id="element-4667" data-mp="2x8qhwaw2998y">Regel</span>
          <span data-id="element-4668" data-mp="2sc03rt3tgnq6" />
        </div>
        <ul className="space-y-2" data-id="element-4669" data-mp="2sayotdjojh36">
          {f.lines.map((l, i) => <li key={l.key} data-id="element-4670" data-mp="36bhw2igbmj4a" data-mpi={i}>
              <div className="grid grid-cols-[minmax(0,1fr)_40px] items-center gap-x-3 gap-y-2 sm:grid-cols-[minmax(0,1fr)_88px_120px_96px_40px]" data-id="element-4671" data-mp="36apyx372sdbw">
                <label className="col-span-1 block" data-id="element-4672" data-mp="1bwyq25wklxhi">
                  <span className="sr-only" data-id="element-4673" data-mp="1bxqkveicfr6q">Artikel, regel {i + 1}</span>
                  <select value={l.itemId} onChange={e => f.setLine(l.key, {
                itemId: e.target.value
              })} className={inputCls} data-id="element-4674" data-mp="16z721mz1qvil">
                    <option value="" data-id="element-4675" data-mp="3im94gu7ai8bo">Kies een artikel…</option>
                    <optgroup label="Producten" data-id="element-4676" data-mp="3nktvq44kdhnz">
                      {retail.map(it => <option key={it.id} value={it.id} data-id="element-4677" data-mp="39tysp8gqxusb">
                          {it.brand ? `${it.brand} · ` : ''}
                          {it.name}
                        </option>)}
                    </optgroup>
                    {consumables.length > 0 && <optgroup label="Verbruik" data-id="element-4678" data-mp="34ulyu3rgjng8">
                        {consumables.map(it => <option key={it.id} value={it.id} data-id="element-4679" data-mp="2qv32e880e0jr">
                            {it.name}
                          </option>)}
                      </optgroup>}
                  </select>
                </label>
                <button type="button" onClick={() => f.removeLine(l.key)} aria-label={`Regel ${i + 1} weghalen`} className="flex h-10 w-10 items-center justify-center rounded-md text-muted transition-colors duration-150 ease-soft hover:bg-sand hover:text-ink sm:order-last" data-id="element-4680" data-mp="p13snrajbr96">
                  <Trash2Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4681" data-mp="1t1bseg7opnas" />
                </button>
                <div className="col-span-2 grid grid-cols-3 items-center gap-3 sm:col-span-1 sm:contents" data-id="element-4682" data-mp="1o3jth28mh0x3">
                  <label className="block" data-id="element-4683" data-mp="2ku2e3wrqrtls">
                    <span className="mb-1 block text-[11px] text-muted sm:sr-only" data-id="element-4684" data-mp="2gq4froa8qmts">Aantal</span>
                    <NumberInput allowEmpty value={l.qty} onChange={v => f.setLine(l.key, {
                  qty: v
                })} min={1} className="text-right" data-id="element-4685" data-mp="2byut8wjznzm4" />
                  </label>
                  <label className="block" data-id="element-4686" data-mp="26yqlpw126f81">
                    <span className="mb-1 block text-[11px] text-muted sm:sr-only" data-id="element-4687" data-mp="34h0gxc0l6udp">Prijs per stuk (€)</span>
                    <NumberInput decimals allowEmpty value={l.cost} onChange={v => f.setLine(l.key, {
                  cost: v
                })} placeholder="excl. btw" className="text-right" data-id="element-4688" data-mp="2zo79ggdqh7xx" />
                  </label>
                  <span className="self-end pb-2 text-right text-[14px] tabular-nums text-ink sm:self-center sm:pb-0" data-id="element-4689" data-mp="3c0j5rywihhu7">
                    {l.qty && l.cost !== undefined ? money(Math.round(l.cost * 100) * l.qty) : '—'}
                  </span>
                </div>
              </div>
              {fieldError(f.show(`lines.${i}`))}
            </li>)}
        </ul>
        {fieldError(f.show('lines'))}
        <button type="button" onClick={f.addLine} className={`${btnGhost} mt-2`} data-id="element-4690" data-mp="3ux15b2hlqm10">
          <PlusIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4691" data-mp="3jem9mgk72n3" />
          Regel toevoegen
        </button>
      </fieldset>

      <div className="grid gap-4 border-t border-line pt-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end" data-id="element-4692" data-mp="8hy6nifi86dc">
        <div className="grid gap-3 sm:grid-cols-3" data-id="element-4693" data-mp="ci9u12z94ksi">
          <label className="block" data-id="element-4694" data-mp="hkhaa4r46n0n">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-4695" data-mp="hjpgmzh2uzd0">Factuurbedrag excl. btw (ter controle)</span>
            <NumberInput decimals allowEmpty value={f.invoiceTotal} onChange={f.setInvoiceTotal} className="w-40" data-id="element-4696" data-mp="12wh2m0z27rvz" />
          </label>
          <label className="block" data-id="element-4697" data-mp="1ct3q6xirsx1p">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-4698" data-mp="1cs213p4x6a0z">Btw op de factuur (voor de boekhouder)</span>
            <NumberInput decimals allowEmpty value={f.invoiceVat} onChange={f.setInvoiceVat} className="w-40" data-id="element-4699" data-mp="17u8wh0j9lo2t" />
          </label>
          <label htmlFor={`${id}-note`} className="block" data-id="element-4700" data-mp="f4tlgarlnv6w">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-4701" data-mp="f6d2w4ozsca9">Notitie (optioneel)</span>
            <input id={`${id}-note`} value={f.note} onChange={e => f.setNote(e.target.value)} maxLength={200} className={inputCls} data-id="element-4702" data-mp="tuhobgffj1wq" />
          </label>
        </div>
        <dl className="text-right text-[14px]" data-id="element-4703" data-mp="1d57e1xsiwna">
          <div className="flex justify-end gap-3" data-id="element-4704" data-mp="1bu40389bqwp">
            <dt className="text-muted" data-id="element-4705" data-mp="3sk8233cu7l29">{f.pieces} stuks, totaal</dt>
            <dd className="tabular-nums text-ink" data-id="element-4706" data-mp="3shvmgwujxmsd">{money(f.totalCents)}</dd>
          </div>
          {f.mismatch !== null && <div className={`mt-1 text-[13px] ${f.mismatch === 0 ? 'text-sage' : 'text-clay'}`} aria-live="polite" data-id="element-4707" data-mp="2csw1kgzsz56z">
              {f.mismatch === 0 ? 'Klopt met de factuur' : `${money(Math.abs(f.mismatch))} ${f.mismatch > 0 ? 'meer' : 'minder'} op de factuur dan in de regels`}
            </div>}
        </dl>
      </div>

      {f.error && <p role="alert" className="text-[13px] text-clay" data-id="element-4708" data-mp="2qsyedot978fd">
          {f.error}
        </p>}

      <div className="flex flex-wrap gap-2" data-id="element-4709" data-mp="2vqgq1myno1r2">
        <button type="submit" disabled={f.submitting} aria-busy={f.submitting} className={btnPrimary} data-id="element-4710" data-mp="2hs09z5ewkyw9">
          {f.submitting && <Spinner data-id="element-4711" data-mp="2ls0zpm7gcwim" />}
          {f.pieces > 0 ? `${f.pieces} ${f.pieces === 1 ? 'stuk' : 'stuks'} boeken op ${locationLabel[f.location].toLowerCase()}` : 'Boeken'}
        </button>
        <button type="button" onClick={onCancel} disabled={f.submitting} className={btnSecondary} data-id="element-4712" data-mp="1aef17afpdikf">
          Annuleren
        </button>
      </div>
    </form>;
}